import React, {FC} from 'react';
import styled from 'styled-components';

import {ClickEventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChildrenCell} from '../../../../components/common/table/cells/childrenCell';
import {DateCell} from '../../../../components/common/table/cells/dateCell';
import {EmptyCell} from '../../../../components/common/table/cells/emptyCell';
import {TextCell} from '../../../../components/common/table/cells/textCell';
import {TableColumn} from '../../../../components/common/table/tableColumn';
import {TableRow, TableRowRenderOptions} from '../../../../components/common/table/tableRow';
import {renderLocalApplicationServerAuthenticationConfigStrategy} from '../../../../helpers/localApplicationHelpers';
import {LocalApplicationServerModel} from '../../../../models/api/localApplicationServerModel';
import {DevelopersServersTableColumnsEnum} from './developersServersTableColumns';

/*
 * Props.
 */

interface DevelopersServersTableRowProps {
  tableColumns: ReadonlyArray<TableColumn<DevelopersServersTableColumnsEnum>>;
  server?: LocalApplicationServerModel;
  options: TableRowRenderOptions;

  onSelectServer: ClickEventHandler;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

const StyledAuthStrategyWrapperDiv = styled.div`
  padding: 0 12px;
`;

/*
 * Component.
 */

export const DevelopersServersTableRow: FC<DevelopersServersTableRowProps> = (props) => {
  const {server, options, tableColumns, onSelectServer} = props;

  if (!server) {
    return null;
  }

  const cellRenderer = (
    index: number,
    column: TableColumn<DevelopersServersTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    switch (column.type) {
      case DevelopersServersTableColumnsEnum.ORIGIN:
        return (
          <TextCell
            key={DevelopersServersTableColumnsEnum.ORIGIN}
            value={server.origin}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case DevelopersServersTableColumnsEnum.AUTH_STRATEGY:
        return (
          <ChildrenCell
            key={DevelopersServersTableColumnsEnum.AUTH_STRATEGY}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          >
            <StyledAuthStrategyWrapperDiv>
              {renderLocalApplicationServerAuthenticationConfigStrategy(server.authenticationConfig.type)}
            </StyledAuthStrategyWrapperDiv>
          </ChildrenCell>
        );
      case DevelopersServersTableColumnsEnum.CREATED_DATE:
        return (
          <DateCell
            key={DevelopersServersTableColumnsEnum.CREATED_DATE}
            value={server.createdAt}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            customFormat={{
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      default: {
        return (
          <EmptyCell settings={column.settings} isHovering={isHovering} size={options.size} index={index} />
        );
      }
    }
  };

  return (
    <StyledTableRow
      onSelect={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        onSelectServer(event);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      hasHover
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
    />
  );
};
